import { type Simplify } from 'type-fest'

type PropAdded<T extends object, K extends string, U> = Simplify<
  T & {
    [key in K]: U
  }
>

/**
 * オブジェクトにプロパティを追加します。
 *
 * @template T - オブジェクトの型。
 * @template K - 追加するプロパティのキーの型。
 * @template U - 追加するプロパティの値の型。
 * @param obj - オブジェクト。
 * @param key - 追加するプロパティのキー。
 * @param fn - オブジェクトを引数に取り、追加するプロパティの値を返す関数。
 * @returns 追加後のオブジェクト。
 */
export function addProp<T extends object, K extends string, U>(
  obj: T,
  key: K,
  fn: (obj: T) => U,
): PropAdded<T, K, U>

/**
 * オブジェクトにプロパティを追加します。
 *
 * @template T - オブジェクトの型。
 * @template K - 追加するプロパティのキーの型。
 * @template U - 追加するプロパティの値の型。
 * @param key - 追加するプロパティのキー。
 * @param fn - オブジェクトを引数に取り、追加するプロパティの値を返す関数。
 * @returns オブジェクトを引数に取り、追加後のオブジェクトを返す関数。
 */
export function addProp<T extends object, K extends string, U>(
  key: K,
  fn: (obj: T) => U,
): (obj: T) => PropAdded<T, K, U>

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function addProp<T extends object, K extends string, U>(
  ...args: [K, (obj: T) => U] | [T, K, (obj: T) => U]
): PropAdded<T, K, U> | ((obj: T) => PropAdded<T, K, U>) {
  const addPropInner = (
    obj: T,
    key: K,
    fn: (obj: T) => U,
  ): PropAdded<T, K, U> =>
    ({
      ...obj,
      [key]: fn(obj),
    }) as PropAdded<T, K, U>

  return args.length === 3
    ? addPropInner(...(args as [T, K, (obj: T) => U]))
    : (obj) => addPropInner(obj, ...(args as [K, (obj: T) => U]))
}
